import { useState } from "react";
import { callFunction } from "../api/callFunction";
import { PrimaryButtonSmall } from "./Button";
import { showToast } from "../utils/showToast";
import PayoutFields, { configToForm, formToWire, summarizeForm, describeSectionWindow } from "./PayoutFields";
import Utility from "../utils/utility";

const formatDate = (ms) => {
  const d = Utility.convertDate(ms);
  return d ? d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—";
};

const describeConfig = (cfg) => {
  if (!cfg) return "—";
  const hostPct = cfg.hostRate != null ? Math.round(cfg.hostRate * 1000) / 10 : "?";
  const min = cfg.minPayout || {};
  const auto = cfg.autoPayout?.enabled ? ", auto payouts" : "";
  const guest = cfg.guestPayouts?.paused
    ? ", guest payouts PAUSED"
    : cfg.guestPayouts?.enabled ? ", guest payouts on" : "";
  if (!min.enabled) return `${hostPct}% host share, no minimum${auto}${guest}`;
  return `${hostPct}% host share, $${(min.amountCents || 0) / 100} min on ${describeSectionWindow(min)} (${min.fundingMode})${auto}${guest}`;
};

/**
 * Modal for the platform-wide payout settings: the host/platform revenue split,
 * the optional new-host boost + minimum guarantee, and the change history.
 * Owns its own form state + save callable; calls `onSaved` so the parent can
 * refresh the payout rows (effective per-host rates change when global changes).
 */
// Matches MAX_SHARE_RATE in the functions repo's users/guestPayouts/guestShare.js.
const MAX_GUEST_SHARE_PCT = 25;

export default function PayoutSettingsModal({ globalCfg, meta, history, onClose, onSaved }) {
  const [form, setForm] = useState(() => configToForm(globalCfg));
  const [saving, setSaving] = useState(false);
  // Guest appearance payouts live on the same config document, so they are
  // edited here — and, more importantly, ROUND-TRIPPED here. The document is
  // full-replaced on save, so a form that did not carry these would delete them.
  const [guestEnabled, setGuestEnabled] = useState(() => globalCfg?.guestPayouts?.enabled === true);
  const [guestPaused, setGuestPaused] = useState(() => globalCfg?.guestPayouts?.paused === true);
  // The platform-wide guest share, as a percent string for the input. Empty
  // means "not set", which the server reads as its own built-in default rather
  // than as zero.
  const [guestSharePct, setGuestSharePct] = useState(() =>
    typeof globalCfg?.guestPayouts?.shareRate === "number"
      ? String(Math.round(globalCfg.guestPayouts.shareRate * 1000) / 10)
      : ""
  );
  // The equal share under which a night is not paid out at all, in dollars for
  // the input. Empty means "not set", which the server reads as its own $5.
  const [guestMinShareDollars, setGuestMinShareDollars] = useState(() =>
    Number.isInteger(globalCfg?.guestPayouts?.minShareCents)
      ? String(globalCfg.guestPayouts.minShareCents / 100)
      : ""
  );

  const handleSave = async () => {
    const pct = Number(form.hostRatePct);
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) { showToast("Host share must be between 0 and 100%."); return; }
    if (form.introEnabled) {
      const ipct = Number(form.introRatePct);
      if (!Number.isFinite(ipct) || ipct < 0 || ipct > 100) { showToast("Boosted host share must be between 0 and 100%."); return; }
      if (!(Number(form.introFirstNEvents) >= 1)) { showToast("Boost: first N events must be at least 1."); return; }
    }
    if (form.minEnabled) {
      if (!(Number(form.minAmountDollars) > 0)) { showToast("Minimum payout must be greater than $0."); return; }
      if (!(Number(form.firstNEvents) >= 1)) { showToast("First N events must be at least 1."); return; }
    }
    const sharePct = guestSharePct.trim() === "" ? null : Number(guestSharePct);
    if (sharePct !== null && (!Number.isFinite(sharePct) || sharePct < 0 || sharePct > MAX_GUEST_SHARE_PCT)) {
      showToast(`Guest share must be between 0 and ${MAX_GUEST_SHARE_PCT}%.`);
      return;
    }
    const minShareCents = guestMinShareDollars.trim() === "" ? null : Math.round(Number(guestMinShareDollars) * 100);
    if (minShareCents !== null && (!Number.isInteger(minShareCents) || minShareCents < 0 || minShareCents > 50000)) {
      showToast("Minimum share must be between $0 and $500.");
      return;
    }
    const guestLine = guestPaused
      ? "Guest appearance payouts: PAUSED (nothing pays, including Pay now)"
      : guestEnabled
        ? "Guest appearance payouts: automatic daily run ON"
        : "Guest appearance payouts: automatic daily run off (Pay now still works)";
    const shareLine = sharePct === null
      ? "Guest share: unchanged"
      : `Guest share: up to ${sharePct}% of an event goes to its guests`;
    const minLine = minShareCents === null
      ? "Minimum share: unchanged"
      : `Minimum share: a night pays nobody if the equal share is under $${(minShareCents / 100).toFixed(2)}`;
    if (!window.confirm(`Apply these payout settings for ALL hosts (unless overridden)?\n\n${summarizeForm(form, { allowCountAnchor: false })}\n${guestLine}\n${shareLine}\n${minLine}`)) return;
    setSaving(true);
    try {
      // Send the version this form was built from: the doc is full-replaced, so
      // without it a second admin editing from a separately-loaded page would
      // silently revert whatever the first one just changed — now including a
      // money-moving toggle. The server rejects a stale save instead.
      await callFunction("adminUpdatePayoutConfig", {
        ...formToWire(form, { allowCountAnchor: false }),
        guestPayouts: {
          enabled: guestEnabled,
          paused: guestPaused,
          // Omitted when blank: the server preserves whatever is stored rather
          // than reading an absent field as zero.
          ...(sharePct === null ? {} : { shareRate: sharePct / 100 }),
          ...(minShareCents === null ? {} : { minShareCents }),
        },
        expectedVersion: meta.version ?? null,
      });
      showToast("Payout settings saved");
      await onSaved();
      onClose();
    } catch (err) {
      console.error("adminUpdatePayoutConfig failed", err);
      showToast(err.message || "Failed to save payout settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-gray-600 bg-opacity-50 p-4 sm:p-6"
      onClick={onClose}
    >
      <div
        className="relative my-4 w-full max-w-2xl rounded-lg bg-white dark:bg-gray-800 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b border-gray-100 dark:border-gray-800 p-5">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Payout Settings</h2>
            <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
              The platform-wide host/platform revenue split and the optional new-host minimum guarantee.
            </p>
          </div>
          <button
            onClick={onClose}
            className="ml-4 flex-shrink-0 rounded-md p-1 text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-600 dark:hover:text-gray-300"
            aria-label="Close"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto p-5">
          {meta.isDefault && (
            <div className="mb-4 rounded-md bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
              No saved config yet — these are the platform defaults (70% host share, no minimum).
              Saving creates the first configuration.
            </div>
          )}

          <PayoutFields value={form} onChange={setForm} idPrefix="global" showAuto />

          <div className="mt-6 border-t border-gray-100 dark:border-gray-800 pt-4">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Guest appearance payouts
            </p>
            <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
              Fees paid to members for showing up to an event. Paid by the platform, so
              this never changes what a host earns. An appearance still needs a person to
              confirm the guest turned up before anything is sent.
            </p>
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
              <input
                id="global-guest-enabled"
                type="checkbox"
                checked={guestEnabled}
                onChange={(e) => setGuestEnabled(e.target.checked)}
                className="rounded border-gray-300 dark:border-gray-600"
              />
              Pay confirmed appearance fees automatically, once they mature (3 days after the event)
            </label>
            <label className="mt-2 flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
              <input
                id="global-guest-paused"
                type="checkbox"
                checked={guestPaused}
                onChange={(e) => setGuestPaused(e.target.checked)}
                className="rounded border-gray-300 dark:border-gray-600"
              />
              Pause all guest payouts (emergency stop — also blocks Pay now)
            </label>
            <div className="mt-3">
              <label htmlFor="global-guest-share" className="block text-sm text-gray-700 dark:text-gray-200">
                Share of an event that can go to its guests
              </label>
              <div className="mt-1 flex items-center gap-2">
                <input
                  id="global-guest-share"
                  type="number"
                  min="0"
                  max={MAX_GUEST_SHARE_PCT}
                  step="1"
                  value={guestSharePct}
                  onChange={(e) => setGuestSharePct(e.target.value)}
                  placeholder="10"
                  className="w-24 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary"
                />
                <span className="text-sm text-gray-500 dark:text-gray-400">%</span>
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                The default for every event. The host is paid first and Gist keeps at least 5%,
                so an event with a boosted host share shares less than this. Max {MAX_GUEST_SHARE_PCT}%.
                A host can be set apart from this, and so can one event.
              </p>
            </div>

            <div className="mt-3">
              <label htmlFor="global-guest-min-share" className="block text-sm text-gray-700 dark:text-gray-200">
                Minimum share per guest
              </label>
              <div className="mt-1 flex items-center gap-2">
                <span className="text-sm text-gray-500 dark:text-gray-400">$</span>
                <input
                  id="global-guest-min-share"
                  type="number"
                  min="0"
                  max="500"
                  step="1"
                  value={guestMinShareDollars}
                  onChange={(e) => setGuestMinShareDollars(e.target.value)}
                  placeholder="5"
                  className="w-24 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                When a night's pool split equally comes to less than this per guest, nobody on that
                night is paid. Set $0 to pay any share Stripe will carry.
              </p>
            </div>

            {guestPaused && (
              <p className="mt-2 rounded-md bg-red-50 dark:bg-red-900/30 px-3 py-2 text-xs text-red-700 dark:text-red-300">
                Paused. No guest is paid, by the daily run or by hand, until this is cleared.
              </p>
            )}
          </div>

          <div className="mt-6 flex items-center justify-between border-t border-gray-100 dark:border-gray-800 pt-4">
            <p className="text-xs text-gray-400 dark:text-gray-500">
              {meta.version != null ? `Version ${meta.version} · ` : ""}
              Last changed {formatDate(meta.updatedAtMs)}
              {meta.updatedBy ? ` by ${meta.updatedBy}` : ""}
            </p>
            <PrimaryButtonSmall onClick={handleSave} loading={saving} loadingLabel="Saving…">
              Save settings
            </PrimaryButtonSmall>
          </div>

          {history.length > 0 && (
            <div className="mt-6 border-t border-gray-100 dark:border-gray-800 pt-4">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Change history</p>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-400 dark:text-gray-500">
                    <th className="py-1 pr-3 font-medium">When</th>
                    <th className="py-1 pr-3 font-medium">Scope</th>
                    <th className="py-1 pr-3 font-medium">Change</th>
                    <th className="py-1 font-medium">By</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h.id} className="border-t border-gray-50 dark:border-gray-800 align-top">
                      <td className="py-1.5 pr-3 text-gray-500 dark:text-gray-400">{formatDate(h.updatedAtMs)}</td>
                      <td className="py-1.5 pr-3 text-gray-500 dark:text-gray-400">
                        {h.scope === "vendor" ? `host ${h.vendorID}` : "global"}
                      </td>
                      <td className="py-1.5 pr-3 text-gray-700 dark:text-gray-200">{describeConfig(h.after)}</td>
                      <td className="py-1.5 truncate text-xs text-gray-400 dark:text-gray-500">{h.updatedBy || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
